/**
 * Component Style Variants
 * 
 * This file defines reusable style variants for various UI components
 * using class-variance-authority (cva).
 *
 * Four live here: textVariants (the header, Plants, the profile),
 * buttonVariants (ui/button, the calendar's day cells, and the `tile` look of ui/TileButton), pillVariants
 * (ui/Pill) and photoButtonVariants (ui/PhotoButton). They are kept out of the
 * component files so those export only components, which keeps hot reload
 * working.
 *
 * Used by: ui/button, ui/Pill, ui/PhotoButton, the calendar, the Header, the
 * plant grid and the profile.
 *
 * @module styles/variants
 */
import { cva } from "class-variance-authority";

/** Text: size, weight, colour (including the wordmark and the nav), tracking, and the theme's serif. */
export const textVariants = cva(
  "text-foreground transition-colors",
  {
    variants: {
      size: {
        xs: "text-xs",
        sm: "text-sm",
        base: "text-base",
        lg: "text-lg",
        xl: "text-xl",
        '2xl': "text-2xl",
        '3xl': "text-3xl",
      },
      weight: {
        light: "font-light",
        normal: "font-normal",
        medium: "font-medium",
        semibold: "font-semibold",
        bold: "font-bold",
      },
      variant: {
        default: "text-foreground",
        muted: "text-muted-foreground",
        primary: "text-primary",
        secondary: "text-secondary",
        destructive: "text-destructive",
        accent: "text-accent",
        // The wordmark is the theme's main colour (Grow's forest, Field notes'
        // pencil, and so on); the database's theme gives it.
        brand: "text-primary",
        // A navigation link: the theme's ink, turning to its accent ink on hover.
        nav: "text-foreground hover:text-accent-ink",
      },
      tracking: {
        normal: "tracking-normal",
        wide: "tracking-wide",
        wider: "tracking-wider",
        widest: "tracking-widest",
      },
      font: {
        default: "",
        serif: "font-display",
      },
    },
    defaultVariants: {
      size: "base",
      weight: "normal",
      variant: "default",
      tracking: "normal",
      font: "default",
    },
    compoundVariants: [
      {
        font: "serif",
        size: "xl",
        className: "grow:text-2xl fieldnotes:text-2xl"
      }
    ]
  }
);

/** shadcn's button looks and sizes (ui/button). */
export const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "underline-offset-4 hover:underline text-primary",
        // Today's rounded-square tile (ui/TileButton): the soft muted fill, 12px corners, the card's ink.
        tile: "relative rounded-xl bg-muted/70 text-foreground font-semibold hover:bg-muted active:bg-muted",
      },
      size: {
        default: "h-10 py-2 px-4",
        sm: "h-9 px-3 rounded-md",
        lg: "h-11 px-8 rounded-md",
        icon: "h-10 w-10", // Adjusted from h-9 w-9 for consistency
        // A tile of the first row (Share, Public): 48px, the icon beside the word.
        tileBig: "h-12 gap-2 rounded-xl px-2 text-[0.9375rem] leading-tight",
        // A tile of a row of three (To-dos, Notes, Doctor, Edit, Postpone, Skip it): 44px, the icon over the word.
        tileSmall: "h-11 flex-col gap-0.5 rounded-xl px-1 py-1 text-[0.8125rem] leading-tight",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

/**
 * The class names for a pill's size and tone (ui/Pill), for anything that
 * should look like one (a router Link, say).
 *
 * The edge is part of every size, transparent unless the tone draws one, so a
 * plain pill and an outlined one are the same size.
 */
export const pillVariants = cva(
  "inline-flex max-w-full items-center rounded-full font-semibold leading-snug",
  {
    variants: {
      size: {
        // The stage pill: 2rem, in rem so it grows with the gardener's text size.
        md: "min-h-8 gap-1 border-[1.5px] px-3 text-[0.8125rem] [&_svg]:h-3.5 [&_svg]:w-3.5",
        // A compact label: a line of text with a little air.
        sm: "gap-1 border px-2 py-px text-xs [&_svg]:h-3.5 [&_svg]:w-3.5",
        // A count: a small round number, centred.
        xs: "h-[1.125rem] min-w-[1.125rem] justify-center border px-1 text-[0.625rem] leading-none tabular-nums",
      },
      tone: {
        // A quiet label: the muted fill.
        neutral: "border-transparent bg-muted text-muted-foreground",
        // A hairline outline and muted words.
        outline: "border-border text-muted-foreground",
        // The theme's primary, tinted.
        primary: "border-transparent bg-primary/10 text-primary",
        // The theme's primary, solid.
        solid: "border-transparent bg-primary text-primary-foreground",
        // An invitation to add: a dashed edge and muted words.
        dashed: "border-dashed border-muted-foreground/60 text-muted-foreground",
        // Share's shine (style.css .share-shine), as on a bed card: no edge, which would show the gradient's other end.
        share: "share-shine border-0",
        // Words on a photograph: dark glass.
        photo: "border-transparent bg-black/55 text-white",
        // Words on a photograph: light glass.
        photoLight: "border-transparent bg-white/90 text-primary shadow-sm",
        // A quiet count on a button's corner: the card's own colours, so it reads as a count and not as something unread.
        count: "border-border bg-card text-muted-foreground",
        // The offer's terracotta: the theme's highlight colour.
        terracotta: "border-transparent bg-highlight text-highlight-foreground",
        // No colours of its own: the caller (a verdict token, a theme) gives them.
        plain: "border-transparent",
      },
    },
    defaultVariants: { size: "md", tone: "neutral" },
  }
);

/**
 * The look of a round glass button or mark on a picture (ui/PhotoButton), by size and tone.
 *
 * The small size's hit area (44px) is drawn by the button itself, with a
 * pseudo-element 8px past the circle on every side.
 */
export const photoButtonVariants = cva(
  "relative flex shrink-0 items-center justify-center rounded-full",
  {
    variants: {
      size: {
        xs: "h-6 w-6",
        sm: "h-7 w-7",
        md: "h-10 w-10",
        // A pill with words beside its icon (Replace, Choose another): 2rem tall, the glass's shape stretched.
        pill: "h-8 gap-1.5 px-3 text-xs font-semibold",
      },
      tone: {
        // Dark glass with a faint white edge, to read on any photograph, light or dark.
        dark: "bg-black/50 text-white shadow-sm ring-1 ring-white/40 backdrop-blur-sm",
        // White glass with dark ink, to read on any photograph, in every theme.
        light: "bg-white/80 text-neutral-900 shadow-sm",
      },
    },
    defaultVariants: { size: "sm", tone: "dark" },
  }
);
